import React, { useState, useRef, useEffect, useMemo } from 'react';
import { MapContainer, TileLayer, Marker, Popup, Polygon, Polyline, useMapEvents, ZoomControl } from 'react-leaflet';
import 'leaflet/dist/leaflet.css';
import L from 'leaflet';
import { Search, MapPin } from 'lucide-react';

// Fix for default marker icon in React-Leaflet
import icon from 'leaflet/dist/images/marker-icon.png';
import iconShadow from 'leaflet/dist/images/marker-shadow.png';

let DefaultIcon = L.icon({
  iconUrl: icon,
  shadowUrl: iconShadow,
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
  shadowSize: [41, 41]
});

L.Marker.prototype.options.icon = DefaultIcon;

// Default center for Mon district, Nagaland
const MON_CENTER = {
  lat: 26.7206,
  lng: 95.1653
};

// Draggable Marker Component for boundary points
function DraggableMarker({ position, index, onDragEnd, editable }) {
  const markerRef = useRef(null);

  const eventHandlers = useMemo(
    () => ({
      dragend() {
        const marker = markerRef.current;
        if (marker != null && onDragEnd) {
          const newPosition = marker.getLatLng();
          onDragEnd(index, newPosition.lat, newPosition.lng);
        }
      },
    }),
    [index, onDragEnd]
  );

  // Create custom draggable circle icon
  const circleIcon = L.divIcon({
    className: 'custom-circle-marker',
    html: `<div style="
      width: 14px;
      height: 14px;
      background-color: #3b82f6;
      border: 2px solid white;
      border-radius: 50%;
      box-shadow: 0 2px 6px rgba(0,0,0,0.4);
      cursor: ${editable ? 'move' : 'pointer'};
      transition: all 0.2s ease;
    " class="draggable-point"></div>`,
    iconSize: [14, 14],
    iconAnchor: [7, 7],
    popupAnchor: [0, -7]
  });

  return (
    <Marker
      draggable={editable}
      eventHandlers={eventHandlers}
      position={position}
      ref={markerRef}
      icon={circleIcon}
    >
      <Popup>
        <div className="text-sm">
          <p className="font-semibold">Boundary Point {index + 1}</p>
          <p className="text-xs text-secondary-600">
            Lat: {position[0].toFixed(6)}
            <br />
            Lng: {position[1].toFixed(6)}
          </p>
          {editable && (
            <p className="text-xs text-blue-600 mt-1 font-medium">
              ↔️ Drag to adjust position
            </p>
          )}
        </div>
      </Popup>
    </Marker>
  );
}

// Component to handle boundary points and map clicks
function BoundaryHandler({ position, setPosition, boundaryPoints, setBoundaryPoints, editable, showCenterOnly }) {
  useMapEvents({
    click(e) {
      if (editable) {
        if (setBoundaryPoints) {
          // Add point to boundary
          const newPoint = { lat: e.latlng.lat, lng: e.latlng.lng };
          setBoundaryPoints([...boundaryPoints, newPoint]);
        } else {
          // Legacy single point mode
          setPosition({
            lat: e.latlng.lat,
            lng: e.latlng.lng
          });
        }
      }
    },
  });

  // For public view with boundary points - show only center marker
  if (showCenterOnly && boundaryPoints && boundaryPoints.length >= 3 && position) {
    return (
      <Marker position={[position.lat, position.lng]}>
        <Popup>
          <div className="text-sm">
            <p className="font-semibold">Permit Location</p>
            <p className="text-xs text-secondary-600">
              Lat: {position.lat.toFixed(6)}
              <br />
              Lng: {position.lng.toFixed(6)}
            </p>
          </div>
        </Popup>
      </Marker>
    );
  }

  // Render boundary polygon or polyline for authenticated users
  if (boundaryPoints && boundaryPoints.length > 0) {
    const positions = boundaryPoints.map(p => [p.lat, p.lng]);

    return (
      <>
        {/* Render polyline/polygon */}
        {boundaryPoints.length >= 3 ? (
          <Polygon
            positions={positions}
            pathOptions={{
              color: '#3b82f6',
              fillColor: '#3b82f6',
              fillOpacity: 0.2,
              weight: 2
            }}
          />
        ) : (
          <Polyline
            positions={positions}
            pathOptions={{
              color: '#3b82f6',
              weight: 2,
              dashArray: '5, 5'
            }}
          />
        )}

        {/* Render draggable markers for each boundary point */}
        {boundaryPoints.map((point, index) => {
          const handleDragEnd = (idx, lat, lng) => {
            const newPoints = [...boundaryPoints];
            newPoints[idx] = { lat, lng };
            setBoundaryPoints(newPoints);
          };

          return (
            <DraggableMarker
              key={index}
              position={[point.lat, point.lng]}
              index={index}
              onDragEnd={handleDragEnd}
              editable={editable}
            />
          );
        })}
      </>
    );
  }

  // Legacy single point marker
  return position ? (
    <Marker position={[position.lat, position.lng]}>
      <Popup>
        <div className="text-sm">
          <p className="font-semibold">Permit Location</p>
          <p className="text-xs text-secondary-600">
            Lat: {position.lat.toFixed(6)}
            <br />
            Lng: {position.lng.toFixed(6)}
          </p>
        </div>
      </Popup>
    </Marker>
  ) : null;
}

const PermitMap = ({
  latitude,
  longitude,
  boundaryPoints = [],
  onChange,
  onBoundaryChange,
  editable = false,
  height = '400px',
  village,
  permitNumber,
  showCenterOnly = false // For public view - only show center marker, not polygon
}) => {
  const [position, setPosition] = useState(
    latitude && longitude ? { lat: parseFloat(latitude), lng: parseFloat(longitude) } : null
  );
  const [localBoundaryPoints, setLocalBoundaryPoints] = useState(boundaryPoints || []);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [showSearchResults, setShowSearchResults] = useState(false);
  const mapRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Update position when props change and center map
  useEffect(() => {
    if (latitude && longitude) {
      const newPos = { lat: parseFloat(latitude), lng: parseFloat(longitude) };
      setPosition(newPos);

      // Center map on the position with high zoom when in edit mode
      if (mapRef.current && editable) {
        mapRef.current.setView([newPos.lat, newPos.lng], 18);
      }
    }
  }, [latitude, longitude, editable]);

  // Update local boundary points when props change (only on initial load, not during editing)
  useEffect(() => {
    if (boundaryPoints && boundaryPoints.length > 0) {
      setLocalBoundaryPoints(boundaryPoints);

      // Center map on boundary points only when not in edit mode (viewing existing permit)
      // This prevents zoom changes when user is actively adding points
      if (mapRef.current && boundaryPoints.length > 0 && !editable) {
        const latSum = boundaryPoints.reduce((sum, p) => sum + parseFloat(p.lat), 0);
        const lngSum = boundaryPoints.reduce((sum, p) => sum + parseFloat(p.lng), 0);
        const centerLat = latSum / boundaryPoints.length;
        const centerLng = lngSum / boundaryPoints.length;
        mapRef.current.setView([centerLat, centerLng], 15);
      }
    }
  }, [boundaryPoints, editable]);

  // Handle position changes
  const handlePositionChange = (newPosition) => {
    setPosition(newPosition);
    if (onChange && editable) {
      onChange(newPosition);
    }
  };

  // Handle boundary points changes
  const handleBoundaryPointsChange = (newPoints) => {
    setLocalBoundaryPoints(newPoints);
    if (onBoundaryChange && editable) {
      onBoundaryChange(newPoints);
    }
  };

  // Clear all boundary points
  const clearBoundaryPoints = () => {
    setLocalBoundaryPoints([]);
    if (onBoundaryChange) {
      onBoundaryChange([]);
    }
  };

  // Remove last boundary point
  const removeLastPoint = () => {
    const newPoints = localBoundaryPoints.slice(0, -1);
    setLocalBoundaryPoints(newPoints);
    if (onBoundaryChange) {
      onBoundaryChange(newPoints);
    }
  };

  // Add new boundary point manually
  const addManualPoint = () => {
    const newPoint = { lat: 0, lng: 0 };
    const newPoints = [...localBoundaryPoints, newPoint];
    setLocalBoundaryPoints(newPoints);
    if (onBoundaryChange) {
      onBoundaryChange(newPoints);
    }
  };

  // Update specific boundary point
  const updateBoundaryPoint = (index, field, value) => {
    const newPoints = [...localBoundaryPoints];
    newPoints[index] = {
      ...newPoints[index],
      [field]: parseFloat(value) || 0
    };
    setLocalBoundaryPoints(newPoints);
    if (onBoundaryChange) {
      onBoundaryChange(newPoints);
    }
  };

  // Remove specific boundary point
  const removeBoundaryPoint = (index) => {
    const newPoints = localBoundaryPoints.filter((_, i) => i !== index);
    setLocalBoundaryPoints(newPoints);
    if (onBoundaryChange) {
      onBoundaryChange(newPoints);
    }
  };

  // Search for location using Nominatim (OpenStreetMap)
  const searchLocation = async (query) => {
    if (!query || query.trim().length < 3) {
      setSearchResults([]);
      setShowSearchResults(false);
      return;
    }

    setIsSearching(true);
    try {
      // Use Nominatim API for geocoding (free, no API key required)
      // Bias search results towards Mon district, Nagaland
      const response = await fetch(
        `https://nominatim.openstreetmap.org/search?` +
        `q=${encodeURIComponent(query + ', Mon, Nagaland, India')}&` +
        `format=json&` +
        `limit=5&` +
        `addressdetails=1&` +
        `bounded=0&` +
        `viewbox=94.5,27.5,95.5,26.0` // Rough bounds for Mon district
      );

      if (response.ok) {
        const data = await response.json();
        setSearchResults(data);
        setShowSearchResults(data.length > 0);
      }
    } catch (error) {
      console.error('Search error:', error);
    } finally {
      setIsSearching(false);
    }
  };

  // Handle search input with debounce
  const handleSearchInput = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    // Clear existing timeout
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    // Debounce search for 500ms
    searchTimeoutRef.current = setTimeout(() => {
      searchLocation(query);
    }, 500);
  };

  // Navigate to selected search result
  const selectSearchResult = (result) => {
    const lat = parseFloat(result.lat);
    const lng = parseFloat(result.lon);

    if (mapRef.current) {
      mapRef.current.setView([lat, lng], 16);
    }

    setShowSearchResults(false);
    setSearchQuery(result.display_name);
  };

  // Get user's current location
  const getCurrentLocation = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const newPos = {
            lat: position.coords.latitude,
            lng: position.coords.longitude
          };
          handlePositionChange(newPos);

          // Center map on new position
          if (mapRef.current) {
            mapRef.current.setView([newPos.lat, newPos.lng], 15);
          }
        },
        (error) => {
          console.error('Error getting location:', error);
          alert('Unable to get your current location. Please click on the map to set the location.');
        }
      );
    } else {
      alert('Geolocation is not supported by your browser.');
    }
  };

  // Calculate polygon area using Shoelace formula (returns area in square meters)
  const calculatePolygonArea = (points) => {
    if (!points || points.length < 3) return 0;

    // Convert lat/lng to meters using Haversine approximation
    const toRadians = (deg) => (deg * Math.PI) / 180;
    const EARTH_RADIUS = 6371000; // meters

    // Calculate area using Shoelace formula with spherical coordinates
    let area = 0;
    const numPoints = points.length;

    for (let i = 0; i < numPoints; i++) {
      const j = (i + 1) % numPoints;
      const lat1 = toRadians(points[i].lat);
      const lat2 = toRadians(points[j].lat);
      const lng1 = toRadians(points[i].lng);
      const lng2 = toRadians(points[j].lng);

      area += (lng2 - lng1) * (2 + Math.sin(lat1) + Math.sin(lat2));
    }

    area = Math.abs((area * EARTH_RADIUS * EARTH_RADIUS) / 2);
    return area;
  };

  // Calculate area for display
  const polygonArea = localBoundaryPoints.length >= 3 ? calculatePolygonArea(localBoundaryPoints) : 0;
  const areaInSqMeters = polygonArea.toFixed(2);
  const areaInSqFeet = (polygonArea * 10.7639).toFixed(2); // Convert to square feet

  // Calculate center based on boundary points or position
  const center = localBoundaryPoints.length > 0
    ? (() => {
        const latSum = localBoundaryPoints.reduce((sum, p) => sum + parseFloat(p.lat), 0);
        const lngSum = localBoundaryPoints.reduce((sum, p) => sum + parseFloat(p.lng), 0);
        return [latSum / localBoundaryPoints.length, lngSum / localBoundaryPoints.length];
      })()
    : position
    ? [position.lat, position.lng]
    : [MON_CENTER.lat, MON_CENTER.lng];

  return (
    <div className="space-y-2">
      {/* CSS for draggable marker hover effect */}
      <style>{`
        .draggable-point:hover {
          transform: scale(1.3);
          box-shadow: 0 3px 8px rgba(0,0,0,0.5) !important;
        }
      `}</style>

      {editable && onBoundaryChange && (
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="flex items-center justify-between mb-2">
            <div className="text-sm text-blue-800">
              <p className="font-medium">Set Boundary Points</p>
              <p className="text-xs">Click on map to add points • Drag points to adjust boundary (min. 3 required)</p>
            </div>
            {localBoundaryPoints.length === 0 && (
              <button
                type="button"
                onClick={addManualPoint}
                className="btn btn-sm bg-green-600 text-white hover:bg-green-700 text-xs"
              >
                + Add Point Manually
              </button>
            )}
          </div>
          {localBoundaryPoints.length > 0 && (
            <div className="flex gap-2 mt-2">
              <button
                type="button"
                onClick={removeLastPoint}
                className="btn btn-sm bg-yellow-600 text-white hover:bg-yellow-700 text-xs"
              >
                Remove Last Point
              </button>
              <button
                type="button"
                onClick={clearBoundaryPoints}
                className="btn btn-sm bg-red-600 text-white hover:bg-red-700 text-xs"
              >
                Clear All Points
              </button>
            </div>
          )}
        </div>
      )}

      {editable && !onBoundaryChange && (
        <div className="flex items-center justify-between bg-blue-50 border border-blue-200 rounded-lg p-3">
          <div className="text-sm text-blue-800">
            <p className="font-medium">Set Permit Location</p>
            <p className="text-xs">Click on the map to mark the location or use your current location</p>
          </div>
          <button
            type="button"
            onClick={getCurrentLocation}
            className="btn btn-sm bg-blue-600 text-white hover:bg-blue-700 text-xs"
          >
            Use My Location
          </button>
        </div>
      )}

      {position && (
        <div className="bg-secondary-50 border border-secondary-200 rounded-lg p-2 text-xs">
          <div className="grid grid-cols-2 gap-2">
            <div>
              <span className="text-secondary-600">Latitude:</span>
              <span className="ml-1 font-mono font-medium">{position.lat.toFixed(6)}</span>
            </div>
            <div>
              <span className="text-secondary-600">Longitude:</span>
              <span className="ml-1 font-mono font-medium">{position.lng.toFixed(6)}</span>
            </div>
          </div>
        </div>
      )}

      {/* Location Search */}
      {editable && (
        <div className="relative z-[40]">
          <div className="flex items-center gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-secondary-400" />
              <input
                type="text"
                value={searchQuery}
                onChange={handleSearchInput}
                placeholder="Search for a location (e.g., village name, landmark)..."
                className="input pl-10 pr-4 w-full text-sm relative z-10 bg-white"
                onFocus={() => searchResults.length > 0 && setShowSearchResults(true)}
              />
              {isSearching && (
                <div className="absolute right-3 top-1/2 transform -translate-y-1/2 z-20">
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-primary-600"></div>
                </div>
              )}
            </div>
          </div>

          {/* Search Results Dropdown */}
          {showSearchResults && searchResults.length > 0 && (
            <div className="absolute z-[41] w-full mt-1 bg-white border border-secondary-300 rounded-lg shadow-2xl max-h-64 overflow-y-auto">
              {searchResults.map((result, index) => (
                <button
                  key={index}
                  type="button"
                  onClick={() => selectSearchResult(result)}
                  className="w-full text-left px-4 py-3 hover:bg-secondary-50 border-b border-secondary-100 last:border-b-0 transition-colors"
                >
                  <div className="flex items-start gap-2">
                    <MapPin className="h-4 w-4 text-primary-600 mt-0.5 flex-shrink-0" />
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-secondary-900 truncate">
                        {result.display_name.split(',')[0]}
                      </p>
                      <p className="text-xs text-secondary-600 truncate">
                        {result.display_name}
                      </p>
                    </div>
                  </div>
                </button>
              ))}
            </div>
          )}

          <p className="text-xs text-secondary-500 mt-1 italic">
            Search to navigate to a location on the map
          </p>
        </div>
      )}

      <div
        className="border-2 border-secondary-300 rounded-lg overflow-hidden"
        style={{ height }}
      >
        <MapContainer
          center={center}
          zoom={position && editable ? 18 : position ? 15 : 10}
          style={{ height: '100%', width: '100%' }}
          ref={mapRef}
          zoomControl={false}
          attributionControl={false}
        >
          <TileLayer
            url="https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}"
            maxZoom={18}
          />
          <ZoomControl position="bottomright" />
          <BoundaryHandler
            position={position}
            setPosition={handlePositionChange}
            boundaryPoints={localBoundaryPoints}
            setBoundaryPoints={handleBoundaryPointsChange}
            editable={editable}
            showCenterOnly={showCenterOnly}
          />
        </MapContainer>
      </div>

      {!editable && !position && (
        <div className="text-center py-8 text-secondary-500">
          <p className="text-sm">No location data available for this permit</p>
        </div>
      )}

      {editable && !onBoundaryChange && (
        <p className="text-xs text-secondary-500 italic">
          {position
            ? 'Click on the map to change the location'
            : 'Click on the map to set the permit location'}
        </p>
      )}

      {/* Manual Boundary Points Editor */}
      {editable && onBoundaryChange && localBoundaryPoints.length > 0 && (
        <div className="bg-white border border-secondary-200 rounded-lg p-4 mt-4">
          <div className="flex items-center justify-between mb-3">
            <h4 className="text-sm font-medium text-secondary-900">Boundary Points Coordinates</h4>
            <button
              type="button"
              onClick={addManualPoint}
              className="btn btn-sm bg-green-600 text-white hover:bg-green-700 text-xs"
            >
              + Add Point
            </button>
          </div>

          <div className="space-y-2 max-h-64 overflow-y-auto">
            {localBoundaryPoints.map((point, index) => (
              <div key={index} className="flex items-center gap-2 p-2 bg-secondary-50 rounded">
                <span className="text-xs font-medium text-secondary-600 w-16">
                  Point {index + 1}:
                </span>
                <div className="flex-1 grid grid-cols-2 gap-2">
                  <div>
                    <label className="text-xs text-secondary-600">Latitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={point.lat || ''}
                      onChange={(e) => updateBoundaryPoint(index, 'lat', e.target.value)}
                      className="input input-sm w-full text-xs"
                      placeholder="Latitude"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-secondary-600">Longitude</label>
                    <input
                      type="number"
                      step="0.000001"
                      value={point.lng || ''}
                      onChange={(e) => updateBoundaryPoint(index, 'lng', e.target.value)}
                      className="input input-sm w-full text-xs"
                      placeholder="Longitude"
                    />
                  </div>
                </div>
                <button
                  type="button"
                  onClick={() => removeBoundaryPoint(index)}
                  className="text-red-600 hover:text-red-800 p-1"
                  title="Remove this point"
                >
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
            ))}
          </div>

          <p className="text-xs text-secondary-500 mt-2 italic">
            Click on map to add points • Drag points on map to adjust • Or manually enter coordinates
          </p>
        </div>
      )}
    </div>
  );
};

export default PermitMap;
